import { Form, InputGroup, Dropdown } from "react-bootstrap";
import { BsSearch, BsSortDown, BsPeople } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import DetailPageHeader from "../common/DetailPageHeader";
import { ExplorerConnectionLimitCounter } from "../Connections/ExplorerConnectionLimitCounter";
import { PAGE_SIZE } from "./eventAttendeesHelpers";

export interface EventAttendeesToolbarProps {
  eventName?: string;
  showPagination: boolean;
  currentPage: number;
  filteredCount: number;
  searchTerm: string;
  searchTargetLang: string;
  onSearchChange: (value: string) => void;
  onSortChange: (key: string) => void;
  showMobileSort: boolean;
  onMobileSortToggle: (nextShow: boolean) => void;
  onBack: () => void;
}

function EventAttendeesToolbar({
  eventName,
  showPagination,
  currentPage,
  filteredCount,
  searchTerm,
  searchTargetLang,
  onSearchChange,
  onSortChange,
  showMobileSort,
  onMobileSortToggle,
  onBack,
}: EventAttendeesToolbarProps) {
  const { t } = useTranslation();

  const sortMenu = (
    <Dropdown.Menu align="end">
      <Dropdown.Item eventKey="connections.sort.recent">
        {t("connections.sort.recent")}
      </Dropdown.Item>
      <Dropdown.Item eventKey="connections.sort.firstName">
        {t("connections.sort.firstName")}
      </Dropdown.Item>
      <Dropdown.Item eventKey="connections.sort.lastName">
        {t("connections.sort.lastName")}
      </Dropdown.Item>
    </Dropdown.Menu>
  );

  const rangeText = showPagination
    ? t("events.attendeesRange", {
        start: (currentPage - 1) * PAGE_SIZE + 1,
        end: Math.min(currentPage * PAGE_SIZE, filteredCount),
        total: filteredCount,
      })
    : t("events.attendeesCount", { count: filteredCount });

  const title = eventName?.trim() || t("events.attendeesTitle");

  const desktopSearchSort = (
    <div className="detail-page-header__toolbar d-flex align-items-center gap-2 w-100 min-w-0">
      <InputGroup className="flex-grow-1 min-w-0 bg-white rounded border overflow-hidden">
        <InputGroup.Text className="bg-white border-0 text-secondary">
          <BsSearch size={16} aria-hidden />
        </InputGroup.Text>
        <Form.Control
          placeholder={t("events.searchByName")}
          lang={searchTargetLang}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="bg-white border-0 shadow-none text-dark"
        />
      </InputGroup>
      <Dropdown onSelect={(k) => k && onSortChange(k)}>
        <Dropdown.Toggle
          variant="outline-light"
          className="shadow-none d-flex align-items-center gap-2 flex-shrink-0 text-nowrap"
        >
          <BsSortDown size={16} aria-hidden />
          <span>{t("connections.sort")}</span>
        </Dropdown.Toggle>
        {sortMenu}
      </Dropdown>
    </div>
  );

  return (
    <>
      <DetailPageHeader
        onBack={onBack}
        eyebrow={t("sidebar.events")}
        title={title}
        meta={
          <>
            <BsPeople size={14} aria-hidden />
            <span>{t("events.attendeesCount", { count: filteredCount })}</span>
          </>
        }
        actions={desktopSearchSort}
        actionsClassName="d-none d-md-block"
      />

      <p className="text-muted small mb-3">{rangeText}</p>

      <ExplorerConnectionLimitCounter className="mb-3" />

      <div className="d-flex d-md-none align-items-center gap-2 mb-3">
        <InputGroup className="rounded-2 flex-grow-1 min-w-0">
          <InputGroup.Text className="bg-white border-end-0 rounded-start">
            <BsSearch size={14} className="text-secondary" />
          </InputGroup.Text>
          <Form.Control
            placeholder={t("events.searchByName")}
            lang={searchTargetLang}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="border-start-0 rounded-end bg-white"
            style={{ minWidth: 0, fontSize: 14 }}
          />
        </InputGroup>
        <div className="d-flex gap-2 ms-auto flex-shrink-0">
          <Dropdown
            show={showMobileSort}
            onToggle={(nextShow) => {
              onMobileSortToggle(nextShow);
            }}
            onSelect={(k) => k && onSortChange(k)}
          >
            <Dropdown.Toggle
              variant="outline-primary"
              className="rounded-2 shadow-none d-flex align-items-center gap-2"
            >
              <BsSortDown size={16} />
              <span>{t("connections.sort")}</span>
            </Dropdown.Toggle>
            {sortMenu}
          </Dropdown>
        </div>
      </div>
    </>
  );
}

export default EventAttendeesToolbar;
